// frontend/src/pages/AdminPanel.tsx
/**
 * Admin Panel - Node Management & Withdrawals
 * 
 * Displays node balances, allows withdrawals to cold storage
 */
import { useAuthStore } from '@/store/useAuthStore';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AdminSecurity from '@/components/admin/AdminSecurity';
import {
  Wallet,
  TrendingUp,
  Server,
  Download,
  Activity,
  DollarSign,
  Bitcoin,
  AlertCircle,
  CheckCircle,
  RefreshCw,
  Eye,
  EyeOff,
} from 'lucide-react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

interface NodeInfo {
  nodeId: string;
  alias: string;
  online: boolean;
  channelCount: number;
}

interface Balances {
  btc: {
    sats: number;
    btc: number;
    usd: number;
  };
  usdt_ta: {
    sats: number;
    tokens: number;
    usd: number;
  };
  usdc_ta: {
    sats: number;
    tokens: number;
    usd: number;
  };
  total_usd: number;
}

interface Stats {
  users: { total: number };
  orders: { total: number };
  swaps: {
    total: number;
    completed: number;
    active: number;
    successRate: string;
  };
  volume: {
    btc: number | any;
    usd: number | any;
  };
  fees: {
    btc: number | any;
    usd: number | any;
  };
}

const AdminPanel: React.FC = () => {
  const [nodeInfo, setNodeInfo] = useState<NodeInfo | null>(null);
  const [balances, setBalances] = useState<Balances | null>(null);
  const [stats, setStats] = useState<Stats | null>(null);
  const [loading, setLoading] = useState(true);
  const [capacity, setCapacity] = useState<{available_sats: number, total_sats: number, locked_sats: number} | null>(null);
  const [showWithdrawModal, setShowWithdrawModal] = useState(false);
  const [withdrawAsset, setWithdrawAsset] = useState('btc');
  const [withdrawAmount, setWithdrawAmount] = useState('');
  const [withdrawing, setWithdrawing] = useState(false);
  const [withdrawSuccess, setWithdrawSuccess] = useState(false);
  const [withdrawTxid, setWithdrawTxid] = useState('');
  const [error, setError] = useState('');
  const [showBalances, setShowBalances] = useState(true);

  useEffect(() => {
    fetchAdminData();
    const interval = setInterval(fetchAdminData, 30000);
    return () => clearInterval(interval);
  }, []);

  const fetchAdminData = async () => {
    try {
      const token = useAuthStore.getState().token;
      if (!token) {
        window.location.href = '/login';
        return;
      }

      const [nodeRes, balancesRes, capacityRes, statsRes] = await Promise.all([
        axios.get(`${API_URL}/admin/node/info`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/admin/node/balances`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/swaps/capacity`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${API_URL}/admin/stats`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setNodeInfo(nodeRes.data.node);
      setBalances(balancesRes.data.balances);
      setStats(statsRes.data.stats);
      if (capacityRes.data?.capacity) setCapacity(capacityRes.data.capacity);
      setLoading(false);
    } catch (error: any) {
      console.error('Failed to fetch admin data:', error);
      if (error.response?.status === 403) {
        setError('Admin access required');
      } else {
        setError('Failed to load admin data');
      }
      setLoading(false);
    }
  };

  const handleWithdraw = async () => {
    if (!withdrawAmount || Number(withdrawAmount) <= 0) {
      alert('Please enter a valid amount');
      return;
    }

    setWithdrawing(true);
    setError('');

    try {
      const token = useAuthStore.getState().token;
      const response = await axios.post(
        `${API_URL}/admin/withdraw`,
        {
          asset: withdrawAsset,
          amount: Number(withdrawAmount),
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      setWithdrawSuccess(true);
      setWithdrawTxid(response.data.withdrawal.txid);
      setWithdrawAmount('');
      
      setTimeout(() => {
        fetchAdminData();
        setShowWithdrawModal(false);
        setWithdrawSuccess(false);
      }, 3000);
    } catch (error: any) {
      console.error('Withdrawal failed:', error);
      setError(error.response?.data?.message || 'Withdrawal failed');
    } finally {
      setWithdrawing(false);
    }
  };

  const formatSats = (sats: number) => {
    return sats.toLocaleString();
  };

  const formatBTC = (btc: number | any) => {
    return Number(btc || 0).toFixed(8);
  };

  const formatUSD = (usd: number | any) => {
    return Number(usd || 0).toLocaleString('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <RefreshCw className="w-12 h-12 text-blue-600 animate-spin mx-auto mb-4" />
          <p className="text-gray-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  if (error && !balances) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <p className="text-red-600 font-semibold">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
            <Server className="w-8 h-8 text-blue-600" />
            Admin Panel
          </h1>
          <p className="text-gray-600 mt-2">Lightning Node Management & Statistics</p>
        </div>

        {/* Node Status */}
        {nodeInfo && (
          <div className="bg-white rounded-lg shadow-md p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                <Activity className="w-5 h-5" />
                Node Status
              </h2>
              <div className="flex items-center gap-2">
                <div className={`w-3 h-3 rounded-full ${nodeInfo.online ? 'bg-green-500' : 'bg-red-500'}`} />
                <span className={`font-semibold ${nodeInfo.online ? 'text-green-600' : 'text-red-600'}`}>
                  {nodeInfo.online ? 'Online' : 'Offline'}
                </span>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <p className="text-sm text-gray-600">Node Alias</p>
                <p className="font-mono text-sm font-semibold">{nodeInfo.alias}</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Node ID</p>
                <p className="font-mono text-xs break-all">{nodeInfo.nodeId.substring(0, 20)}...</p>
              </div>
              <div>
                <p className="text-sm text-gray-600">Active Channels</p>
                <p className="font-semibold text-lg">{nodeInfo.channelCount}</p>
              </div>
            </div>
          </div>
        )}

        {/* Balances */}
        {balances && (
          <div className="bg-gradient-to-br from-blue-600 to-blue-800 rounded-lg shadow-lg p-6 mb-6 text-white">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-2xl font-bold flex items-center gap-2">
                <Wallet className="w-6 h-6" />
                Node Balances
              </h2>
              <div className="flex items-center gap-4">
                <button
                  onClick={() => setShowBalances(!showBalances)}
                  className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
                >
                  {showBalances ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  {showBalances ? 'Hide' : 'Show'}
                </button>
                <button
                  onClick={() => setShowWithdrawModal(true)}
                  className="flex items-center gap-2 bg-white text-blue-600 hover:bg-blue-50 px-6 py-2 rounded-lg font-semibold transition-colors"
                >
                  <Download className="w-4 h-4" />
                  Withdraw
                </button>
              </div>
            </div>

            {showBalances ? (
              <>
                {/* Total Balance */}
                <div className="bg-white/10 rounded-lg p-6 mb-6">
                  <p className="text-sm opacity-90 mb-2">Total Value (USD)</p>
                  <p className="text-4xl font-bold">{formatUSD(balances.total_usd)}</p>
                </div>

                {/* Individual Balances */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {/* BTC */}
                  <div className="bg-white/10 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Bitcoin className="w-5 h-5" />
                      <h3 className="font-semibold">Bitcoin</h3>
                    </div>
                    <p className="text-2xl font-bold mb-1">{formatSats(balances.btc.sats)}</p>
                    <p className="text-sm opacity-90">SATS</p>
                    <p className="text-sm opacity-75 mt-2">{formatBTC(balances.btc.btc)} BTC</p>
                    <p className="text-sm opacity-75">{formatUSD(balances.btc.usd)}</p>
                  </div>

                  {/* USDT-TA */}
                  <div className="bg-white/10 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <DollarSign className="w-5 h-5" />
                      <h3 className="font-semibold">USDT (Taproot)</h3>
                    </div>
                    <p className="text-2xl font-bold mb-1">{formatSats(balances.usdt_ta.sats)}</p>
                    <p className="text-sm opacity-90">SATS</p>
                    <p className="text-sm opacity-75 mt-2">{balances.usdt_ta.tokens.toLocaleString()} USDT</p>
                    <p className="text-sm opacity-75">{formatUSD(balances.usdt_ta.usd)}</p>
                  </div>

                  {/* USDC-TA */}
                  <div className="bg-white/10 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <DollarSign className="w-5 h-5" />
                      <h3 className="font-semibold">USDC (Taproot)</h3>
                    </div>
                    <p className="text-2xl font-bold mb-1">{formatSats(balances.usdc_ta.sats)}</p>
                    <p className="text-sm opacity-90">SATS</p>
                    <p className="text-sm opacity-75 mt-2">{balances.usdc_ta.tokens.toLocaleString()} USDC</p>
                    <p className="text-sm opacity-75">{formatUSD(balances.usdc_ta.usd)}</p>
                  </div>
                </div>
              </>
            ) : (
              <div className="text-center py-12">
                <EyeOff className="w-16 h-16 opacity-50 mx-auto mb-4" />
                <p className="text-lg opacity-75">Balances hidden for privacy</p>
              </div>
            )}
          </div>
        )}

        {/* Platform Statistics */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
            {/* Users */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Total Users</p>
                <TrendingUp className="w-5 h-5 text-blue-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{stats.users.total}</p>
            </div>

            {/* Orders */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Total Orders</p>
                <Activity className="w-5 h-5 text-green-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{stats.orders.total}</p>
            </div>

            {/* Swaps */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Completed Swaps</p>
                <CheckCircle className="w-5 h-5 text-purple-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{stats.swaps.completed}</p>
              <p className="text-xs text-gray-500 mt-1">{stats.swaps.successRate}% success rate</p>
            </div>

            {/* Volume */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-2">
                <p className="text-sm text-gray-600">Total Volume</p>
                <DollarSign className="w-5 h-5 text-orange-600" />
              </div>
              <p className="text-3xl font-bold text-gray-900">{formatUSD(stats.volume.usd)}</p>
              <p className="text-xs text-gray-500 mt-1">{formatBTC(stats.volume.btc)} BTC</p>
            </div>
          </div>
        )}

        {/* Fees Earned */}
        {stats && (
          <div className="bg-green-50 border-2 border-green-200 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-green-900 mb-4 flex items-center gap-2">
              <TrendingUp className="w-5 h-5" />
              Platform Revenue (Fees Collected)
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <p className="text-sm text-green-700">BTC Fees</p>
                <p className="text-2xl font-bold text-green-900">{formatBTC(stats.fees.btc)} BTC</p>
              </div>
              <div>
                <p className="text-sm text-green-700">USD Equivalent</p>
                <p className="text-2xl font-bold text-green-900">{formatUSD(stats.fees.usd)}</p>
              </div>
            </div>
          </div>
        )}

        {/* P&L Summary */}
        {stats && (
          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-lg p-6">
            <h2 className="text-xl font-semibold text-blue-900 mb-4 flex items-center gap-2">
              <DollarSign className="w-5 h-5" />
              DEX Profit & Loss
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Revenue (Fees)</p>
                <p className="text-2xl font-bold text-green-600">{Math.round(Number(stats.fees.btc) * 100000000).toLocaleString()} sats</p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Routing Costs (est.)</p>
                <p className="text-2xl font-bold text-red-500">~{Math.round(stats.swaps.completed * 2)} sats</p>
                <p className="text-xs text-gray-400">~2 sats/trade avg</p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Net Profit</p>
                <p className="text-2xl font-bold text-blue-600">{(Math.round(Number(stats.fees.btc) * 100000000) - Math.round(stats.swaps.completed * 2)).toLocaleString()} sats</p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="text-xs text-gray-500 uppercase tracking-wide">Profit Margin</p>
                <p className="text-2xl font-bold text-blue-600">{Number(stats.fees.btc) > 0 ? ((1 - (stats.swaps.completed * 2) / (Number(stats.fees.btc) * 100000000)) * 100).toFixed(1) : 0}%</p>
              </div>
              <div className="bg-white rounded-lg p-4 shadow-sm">
                <p className="text-xs text-gray-500 uppercase tracking-wide">DEX Liquidity</p>
                <p className="text-2xl font-bold text-indigo-600">{balances ? balances.btc.sats.toLocaleString() : '—'} sats</p>
                <p className="text-xs text-gray-400">{balances ? "$" + balances.btc.usd.toFixed(2) : ""}</p>
                <p className="text-xs text-green-600 font-medium mt-1">{capacity ? '⚡ Max trade: ' + capacity.available_sats.toLocaleString() + ' sats' : ''}</p>
              </div>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 border-t border-blue-200 pt-4">
              <div>
                <p className="text-xs text-gray-500">Total Volume</p>
                <p className="text-lg font-semibold text-gray-800">{Math.round(Number(stats.volume.btc) * 100000000).toLocaleString()} sats</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Completed Trades</p>
                <p className="text-lg font-semibold text-gray-800">{stats.swaps.completed}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Avg Fee / Trade</p>
                <p className="text-lg font-semibold text-gray-800">{stats.swaps.completed > 0 ? Math.round(Number(stats.fees.btc) * 100000000 / stats.swaps.completed) : 0} sats</p>
              </div>
            </div>
          </div>
        )}

        {/* Security */}
        <AdminSecurity />
      </div>

      {/* Withdrawal Modal */}
      {showWithdrawModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Download className="w-5 h-5" />
                Withdraw to Cold Wallet
              </h3>
              <button
                onClick={() => setShowWithdrawModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                ✕
              </button>
            </div>

            {!withdrawSuccess ? (
              <>
                {/* Asset Selection */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Asset
                  </label>
                  <select
                    value={withdrawAsset}
                    onChange={(e) => setWithdrawAsset(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="btc">Bitcoin (BTC)</option>
                    <option value="usdt_ta">USDT (Taproot Assets)</option>
                    <option value="usdc_ta">USDC (Taproot Assets)</option>
                  </select>
                </div>

                {/* Amount Input */}
                <div className="mb-4">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Amount (SATS)
                  </label>
                  <input
                    type="number"
                    value={withdrawAmount}
                    onChange={(e) => setWithdrawAmount(e.target.value)}
                    placeholder="1000000"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Minimum: 1,000,000 sats (0.01 BTC)
                  </p>
                </div>

                {/* Destination */}
                <div className="mb-6">
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Destination
                  </label>
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                    <p className="text-xs font-mono text-gray-600 break-all">
                      {import.meta.env.VITE_COLD_WALLET_ADDRESS || 'bc1q...configured-in-env'}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">Cold Wallet (from .env)</p>
                  </div>
                </div>

                {error && (
                  <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                )}

                {/* Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowWithdrawModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-semibold transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleWithdraw}
                    disabled={withdrawing}
                    className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {withdrawing ? (
                      <>
                        <RefreshCw className="w-4 h-4 animate-spin" />
                        Processing...
                      </>
                    ) : (
                      <>
                        <Download className="w-4 h-4" />
                        Withdraw
                      </>
                    )}
                  </button>
                </div>
              </>
            ) : (
              <div className="text-center py-6">
                <CheckCircle className="w-16 h-16 text-green-600 mx-auto mb-4" />
                <h4 className="text-xl font-bold text-gray-900 mb-2">
                  Withdrawal Successful!
                </h4>
                <p className="text-gray-600 mb-4">
                  Your withdrawal has been processed
                </p>
                <div className="bg-gray-50 rounded-lg p-3 mb-4">
                  <p className="text-xs text-gray-600 mb-1">Transaction ID:</p>
                  <p className="text-xs font-mono text-gray-900 break-all">{withdrawTxid}</p>
                </div>
                <p className="text-sm text-gray-500">Closing in 3 seconds...</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminPanel;// cache bust Thu 19 Mar 2026 18:30:27 AEDT
